import React, { useEffect, useState } from "react";
import { ArrowDownward, ArrowUpward } from "@mui/icons-material";
import { MenuItem, Select } from "@mui/material";
import moment from "moment";
import SEO from "../../Common/SEO";
import FullPageProgress from "../../FullPageProgress";
import { ToastManager } from "../../Toast/ToastManager";
import CompanyChartAbout from "./CompanyChartAbout";
import CompanyChartGraph from "./CompanyChartGraph";
import CompanyChartKey from "./CompanyChartKey";
import { CURRENCY_FORMAT_PRICE, CURRENCY_SYMBOL } from "../../../common/utils";
import { EXCHANGE_SERVICES } from "../../../services";
import "./style.scss";

const CompanyCharts = props => {
  const { orgStocks, orgName, orgLogoColor } = props;
  const [selectedStock, setSelectedStock] = useState("");
  const [type, setType] = useState("5_DAY");
  const [graphData, setGraphData] = useState("");
  const [chartData, setChartData] = useState("");
  const [loading, setLoading] = useState(false);

  const [graphType, setGraphType] = useState("date");

  const typeList = [
    {
      title: "1 day",
      to: "DAY",
    },
    {
      title: "5 days",
      to: "5_DAY",
    },
    {
      title: "1 month",
      to: "MONTH",
    },
    {
      title: "6 months",
      to: "6_MONTH",
    },
    {
      title: "Year to date",
      to: "YTD",
    },
    {
      title: "1 year",
      to: "YEAR",
    },
    {
      title: "5 year",
      to: "5_YEAR",
    },
    {
      title: "All time",
      to: "ALL",
    },
  ];

  useEffect(() => {
    if (!selectedStock) return;
    setLoading(true);
    EXCHANGE_SERVICES.stockOverview({
      exSystem: selectedStock?.exSystem,
      exchange: selectedStock?.exchange,
      symbol: selectedStock?.symbol,
    })
      .then(data => {
        if (data.code === 200) {
          setChartData({
            overview: data?.data?.overview,
            live: data?.data?.live,
            stock: {
              ...selectedStock,
              currencySymbol: CURRENCY_SYMBOL(selectedStock?.currency),
            },
          });
        } else {
          ToastManager.addToast({ title: data.message });
        }
      })
      .catch(err => {
        ToastManager.addToast({ title: err.message });
      })
      .finally(() => {
        setLoading(false);
      });
  }, [selectedStock]);

  useEffect(() => {
    if (!selectedStock || !type) return;
    let from;
    if (type === "DAY") {
      let dayStart = moment().subtract(1, "days").startOf("DAY");
      from = dayStart.unix();
    } else if (type === "5_DAY") {
      let dayStart = moment().subtract(5, "days").startOf("day");
      from = dayStart.unix();
    } else if (type === "MONTH") {
      let dayStart = moment().subtract(1, "month").startOf("day");
      from = dayStart.unix();
    } else if (type === "6_MONTH") {
      let dayStart = moment().subtract(6, "month").startOf("day");
      from = dayStart.unix();
    } else if (type === "YTD") {
      from = moment().startOf("year").format("YYYY-MM-DD");
    } else if (type === "YEAR") {
      from = moment().subtract(1, "year").startOf("day").format("YYYY-MM-DD");
    } else if (type === "5_YEAR") {
      from = moment().subtract(5, "year").startOf("day").format("YYYY-MM-DD");
    } else if (type === "ALL") {
      from = "ALL";
    }

    EXCHANGE_SERVICES.stockChart({
      exSystem: selectedStock?.exSystem,
      exchange: selectedStock?.exchange,
      symbol: selectedStock?.symbol,
      type: type,
      from: from,
    })
      .then(data => {
        if (data.code === 200) {
          setGraphData(data?.data?.list || []);
          setGraphType(data?.data?.graphType);
        } else {
          ToastManager.addToast({ title: data.message });
        }
      })
      .catch(err => {
        ToastManager.addToast({ title: err.message });
      });
  }, [selectedStock, type]);
  useEffect(() => {
    if (!orgStocks) return;
    let stock = orgStocks?.find(stock => stock.primary === true);
    setSelectedStock(stock);
  }, [orgStocks]);

  const onChangeStock = data => {
    if (data) {
      let [symbol, exchange] = data.split("|||");

      let stock = orgStocks?.find(
        stock => stock.symbol === symbol && stock.exchange === exchange
      );
      setType("5_DAY");
      setSelectedStock(stock);
    }
  };

  const liveTime = timestmp => {
    const dateToCheck = moment(timestmp * 1000);
    if (dateToCheck.isSame(moment(), "day")) {
      return `As of today at ${dateToCheck.format("hh:mm A")}`;
    } else if (dateToCheck.isSame(moment().subtract(1, "day"), "day")) {
      return `As of yesterday at ${dateToCheck.format("hh:mm A")}`;
    } else {
      return `As of ${dateToCheck.format("ddd")} at ${dateToCheck.format(
        "hh:mm A"
      )}`;
    }
  };

  return (
    <React.Fragment>
      <SEO title={`${orgName || "Profile"} | Charts | 6ix`} />
      {selectedStock && chartData ? (
        <div className="event-page-company-chart">
          <div className="event-page-company-chart-top">
            {chartData?.overview?.logo ? (
              <div
                className="event-page-company-chart-top-logo"
                style={{
                  backgroundColor: orgLogoColor ? orgLogoColor : "",
                }}
              >
                <img src={chartData?.overview?.logo} alt="" />
              </div>
            ) : null}
            <div className="event-page-company-chart-top-content">
              <div className="event-page-company-chart-top-content-name">
                {chartData?.overview?.name}
              </div>
              <div className="event-page-company-chart-top-content-exchange">
                <Select
                  className="custom-select"
                  sx={{
                    height: "42px",
                    background: "transparent !important",
                    width: "100%",
                  }}
                  value={`${selectedStock?.symbol}|||${selectedStock?.exchange}`}
                  onChange={e => onChangeStock(e.target.value)}
                  displayEmpty
                >
                  <MenuItem disabled>Select Exchange</MenuItem>
                  {orgStocks?.map((item, key) => {
                    return (
                      <MenuItem
                        key={key}
                        name={item.exchange}
                        value={`${item.symbol}|||${item.exchange}`}
                      >
                        {item?.symbol} - {item?.exchange} ({item?.country})
                      </MenuItem>
                    );
                  })}
                </Select>
              </div>
              {chartData?.live ? (
                <>
                  <div className="event-page-company-chart-top-content-price">
                    {chartData?.live?.close !== "NA" ? (
                      <div className="event-page-company-chart-top-content-price-vol">
                        {CURRENCY_FORMAT_PRICE(
                          chartData?.live?.close,
                          chartData?.stock?.currency
                        )}{" "}
                        <span>{chartData?.stock?.currency}</span>
                      </div>
                    ) : null}
                    {chartData?.live?.change !== "NA" ? (
                      <div
                        className={` event-page-company-chart-top-content-price-percent ${
                          chartData?.live?.change < 0 ? "negative" : ""
                        }`}
                      >
                        {chartData?.live?.change > 0 ? (
                          <ArrowUpward />
                        ) : chartData?.live?.change < 0 ? (
                          <ArrowDownward />
                        ) : (
                          ""
                        )}
                        {chartData?.live?.change} ({chartData?.live?.change_p}%
                        )
                      </div>
                    ) : null}
                  </div>

                  {chartData?.live?.timestamp &&
                  chartData?.live?.timestamp !== "NA" ? (
                    <div className="event-page-company-chart-top-content-text">
                      {liveTime(chartData?.live?.timestamp)}
                    </div>
                  ) : null}
                </>
              ) : null}
            </div>
          </div>

          <div className="events6ix-layout-page-header-menu">
            <div className="events6ix-layout-page-header-menu-link active">
              Overview
            </div>
          </div>

          {/* {graphData ? ( */}
          <div className="event-page-company-chart-graph">
            <div className="event-page-company-chart-graph-content">
              <CompanyChartGraph
                stock={chartData?.stock || selectedStock}
                graphData={graphData}
                graphType={graphType}
              />
            </div>
            <div className="event-page-company-chart-graph-bottom">
              {typeList?.map((item, key) => (
                <div
                  key={key}
                  className={`event-page-company-chart-graph-bottom-col ${
                    item?.to === type ? "active" : ""
                  }`}
                  onClick={() => setType(item?.to)}
                >
                  <div className="event-page-company-chart-graph-bottom-col-text">
                    {item?.title}
                  </div>
                </div>
              ))}
            </div>
          </div>
          {/* ) : null} */}

          <CompanyChartKey chartData={chartData} />
          <CompanyChartAbout chartData={chartData} />
        </div>
      ) : loading ? (
        <FullPageProgress />
      ) : null}
    </React.Fragment>
  );
};

export default CompanyCharts;
